import { useEffect } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useSharedStyles } from "./sharedStyles";
import type { RiskSummary } from "./types";
import { GEWERBE_TYPEN, STELLPLATZ_ARTEN } from "../risikoImmobilieStep/enums";
import {
  asStellplatzArten,
  asStellplatzDetails,
  stellplatzArtKey,
  stellplatzDetailText,
  type StellplatzDetails,
} from "../risikoImmobilieStep/stellplatzTypes";
import {
  buildGoogleEarthUrl,
  buildTimOnlineUrl,
  formatFullAddress,
  timOnlineMoeglich,
  buildHochwasserCheckUrl,
  hochwasserCheckMoeglich,
  HOCHWASSER_HINWEIS,
} from "../../utils/geoLinks";
import { useGeocodedAddress } from "../forms/google/useGeocodedAddress";

const STELLPLATZ_LIST: string[] = [...STELLPLATZ_ARTEN];

// ── RisikoanschriftCard ────────────────────────────────────────
const useAnschriftStyles = makeStyles({
  skizzeFrame: {
    width: "100%",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  skizzeBody: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fafafa",
    minHeight: "200px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", "#e3dee7"),
  },
  skizzeCaption: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
    textAlign: "center",
  },
  addressRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "3fr 1fr 2fr",
    },
  },
  addressItem: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  addressLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  addressValue: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  linkRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "10px",
    rowGap: "8px",
    paddingTop: "4px",
  },
  linkBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", "#dcd6e1"),
    textDecorationLine: "none",
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.purple),
    },
  },
});

export const RisikoanschriftCard = ({
  risk,
  geocode = false,
}: {
  risk: RiskSummary;
  /**
   * Adresse geokodieren (3D-Schrägansicht in Google Earth).
   * MUSS von außen gesteuert werden: `RisikoinfoSection` rendert diese Karte
   * einmal für das Hauptrisiko und einmal je temporär vermietetem Risiko, und
   * die `<details>` sind eager gemountet — ohne dieses Gate gingen beim Öffnen
   * des Abschnitts 1+N Geocode-Anfragen für nie aufgeklappte Einträge raus.
   */
  geocode?: boolean;
}) => {
  const styles = useAnschriftStyles();
  const card = useSharedStyles();
  const adresse = {
    street: risk.street,
    postalCode: risk.postalCode,
    city: risk.city,
    country: risk.country || "Deutschland",
  };
  const fullAddress = formatFullAddress(adresse);
  const coords = useGeocodedAddress(adresse, geocode);
  // Vorher zeigte dieser Link auf Google MAPS statt Earth …
  const earthUrl = buildGoogleEarthUrl(fullAddress, coords);
  // … und dieser auf ein "?searchterm=", das es bei TIM-online nicht gibt.
  const timUrl = buildTimOnlineUrl(adresse);
  const hochwasserUrl = buildHochwasserCheckUrl(adresse);

  return (
    <div className={card.cardSection}>
      <div className={styles.skizzeFrame}>
        <div className={styles.skizzeCaption}>Skizze (Risikoobjekt)</div>
        <div
          className={styles.skizzeBody}
          aria-label="Skizze des Gebäudes"
          dangerouslySetInnerHTML={
            risk.skizzeSvg
              ? { __html: risk.skizzeSvg }
              : { __html: '<span style="color:#9b94a3;font-size:0.85rem;">Keine Skizze hinterlegt</span>' }
          }
        />
      </div>
      <div className={card.card}>
        <div className={styles.addressRow}>
          <div className={styles.addressItem}>
            <span className={styles.addressLabel}>Straße & Hausnummer</span>
            <span className={styles.addressValue}>{risk.street || "–"}</span>
          </div>
          <div className={styles.addressItem}>
            <span className={styles.addressLabel}>PLZ</span>
            <span className={styles.addressValue}>{risk.postalCode || "–"}</span>
          </div>
          <div className={styles.addressItem}>
            <span className={styles.addressLabel}>Ort</span>
            <span className={styles.addressValue}>{risk.city || "–"}</span>
          </div>
        </div>
        <div className={styles.linkRow}>
          <a className={styles.linkBtn} href={earthUrl} target="_blank" rel="noreferrer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke={BRAND.purple} strokeWidth="2" />
              <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" stroke={BRAND.purple} strokeWidth="1.5" />
            </svg>
            In Google Earth ansehen
          </a>
          {timOnlineMoeglich(adresse) && (
            <a className={styles.linkBtn} href={timUrl} target="_blank" rel="noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 21V9l9-6 9 6v12h-6v-7H9v7H3z" stroke={BRAND.purple} strokeWidth="2" strokeLinejoin="round" />
              </svg>
              TIM-online ansehen
            </a>
          )}
          {/* T-339: GDV-Hochwasser-Check, nur verlinkt; ZÜRS-Hinweis im Tooltip. */}
          {hochwasserCheckMoeglich(adresse) && (
            <a className={styles.linkBtn} href={hochwasserUrl} target="_blank" rel="noopener noreferrer" title={HOCHWASSER_HINWEIS}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.6C6 4.4 4.6 6.3 4.6 8.1a3.4 3.4 0 0 0 6.8 0C11.4 6.3 10 4.4 8 1.6z" stroke={BRAND.purple} strokeWidth="1.4" />
                <path d="M1.4 14c1.1 0 1.6-.8 2.2-.8s1.1.8 2.2.8 1.6-.8 2.2-.8 1.1.8 2.2.8 1.6-.8 2.2-.8 1.1.8 2.2.8" stroke={BRAND.purple} strokeWidth="1.2" />
              </svg>
              Hochwasser-Check
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ── EigentuemerBlock ────────────────────────────────────────
const useEigentuemerStyles = makeStyles({
  row: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  tile: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  tileCombined: {
    backgroundColor: "#f5eef6",
    ...shorthands.border("1px", "dashed", BRAND.purple),
  },
  avatar: {
    width: "48px",
    height: "48px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  body: { display: "flex", flexDirection: "column", rowGap: "4px", minWidth: 0 },
  name: { fontWeight: 700, fontSize: "1rem", color: BRAND.ink },
  pills: { display: "flex", flexWrap: "wrap", columnGap: "6px", rowGap: "4px" },
  pill: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    color: BRAND.white,
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  pillSecondary: {
    backgroundColor: BRAND.purple,
  },
});

export const EigentuemerBlock = ({ risk }: { risk: RiskSummary }) => {
  const styles = useEigentuemerStyles();
  const owner = risk.contacts.find((c) => c.role === "Eigentümer") ?? risk.contacts[0];
  const vn = risk.contacts.find((c) => c.role === "Versicherungsnehmer") ?? owner;
  const initials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase() || "?";
  const sameContact = owner && vn && owner.id === vn.id;

  if (!owner) {
    return (
      <div style={{ color: BRAND.muted, fontSize: "0.9rem" }}>
        Kein Eigentümer / Versicherungsnehmer am Risikoobjekt hinterlegt.
      </div>
    );
  }

  if (sameContact) {
    return (
      <div className={mergeClasses(styles.tile, styles.tileCombined)}>
        <div className={styles.avatar}>{initials(owner.displayName)}</div>
        <div className={styles.body}>
          <div className={styles.name}>{owner.displayName}</div>
          <div className={styles.pills}>
            <span className={styles.pill}>Eigentümer</span>
            <span className={mergeClasses(styles.pill, styles.pillSecondary)}>Versicherungsnehmer</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <div className={styles.tile}>
        <div className={styles.avatar}>{initials(owner.displayName)}</div>
        <div className={styles.body}>
          <div className={styles.name}>{owner.displayName}</div>
          <div className={styles.pills}>
            <span className={styles.pill}>Eigentümer</span>
          </div>
        </div>
      </div>
      <div className={styles.tile}>
        <div className={styles.avatar}>{vn ? initials(vn.displayName) : "?"}</div>
        <div className={styles.body}>
          <div className={styles.name}>{vn?.displayName ?? "–"}</div>
          <div className={styles.pills}>
            <span className={mergeClasses(styles.pill, styles.pillSecondary)}>Versicherungsnehmer</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── VersicherteSacheBlock ────────────────────────────────────────
const useVsStyles = makeStyles({
  list: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 540px)": { gridTemplateColumns: "1fr 1fr" },
    "@media (min-width: 880px)": { gridTemplateColumns: "repeat(3, 1fr)" },
  },
  feature: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  iconBox: {
    width: "40px",
    height: "40px",
    backgroundColor: "#f5eef6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...shorthands.borderRadius("8px"),
  },
  text: { display: "flex", flexDirection: "column", rowGap: "2px" },
  name: { fontWeight: 700, fontSize: "0.92rem", color: BRAND.ink },
  hint: { fontSize: "0.78rem", color: BRAND.muted },
  addressLine: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
  },
});

/**
 * Merkmalswerte kommen hier als Rohtext an (`Record<string, string>`, kein
 * Rück-Parse). `stellplatzArten` steht als JSON-Array in der Datenbank; ältere
 * Datensätze und von Hand gepflegte Werte auch als Komma-Liste. Beides lesen.
 */
const parseStellplatzArten = (raw: string | undefined): string[] => {
  const t = (raw ?? "").trim();
  if (t === "") return [];
  if (t.startsWith("[")) {
    const aus = asStellplatzArten(t);
    if (aus.length > 0) return aus.map((s) => s.trim()).filter(Boolean);
  }
  return t.split(",").map((s) => s.trim()).filter(Boolean);
};

const parseStellplatzDetails = (raw: string | undefined): StellplatzDetails =>
  asStellplatzDetails(raw ?? "");

const featureIcon = (kind: string) => {
  if (kind === "anbau")
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 21V11l8-6 8 6v10h-5v-6h-6v6H3z" stroke={BRAND.purple} strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M19 13h2v8h-2z" fill={BRAND.gold} />
      </svg>
    );
  if (kind === "garage")
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 21V11l9-6 9 6v10" stroke={BRAND.purple} strokeWidth="1.8" />
        <rect x="6" y="13" width="12" height="8" stroke={BRAND.purple} strokeWidth="1.5" fill={BRAND.gold} fillOpacity="0.3" />
      </svg>
    );
  if (kind === "carport")
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 12l9-6 9 6" stroke={BRAND.purple} strokeWidth="1.8" />
        <path d="M5 12v9M19 12v9" stroke={BRAND.purple} strokeWidth="1.8" />
      </svg>
    );
  if (kind === "tiefgarage")
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 14h18M3 14l4-6h10l4 6M5 14v6h4v-3h6v3h4v-6" stroke={BRAND.purple} strokeWidth="1.6" fill="none" />
      </svg>
    );
  if (kind === "flaeche")
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="14" stroke={BRAND.purple} strokeWidth="1.6" />
        <path d="M3 9h18M9 5v14" stroke={BRAND.gold} strokeWidth="1.4" />
      </svg>
    );
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="4" width="16" height="16" stroke={BRAND.purple} strokeWidth="1.6" />
    </svg>
  );
};

export const VersicherteSacheBlock = ({ risk }: { risk: RiskSummary }) => {
  const styles = useVsStyles();
  const card = useSharedStyles();
  const c = risk.characteristics;
  const truthy = (s: string | undefined) => s === "true" || s === "ja" || s === "Ja";
  const features: Array<{ kind: string; name: string; hint?: string }> = [];
  if (truthy(c.anbautenJa) || truthy(c.anbauten)) features.push({ kind: "anbau", name: "Anbauten / Nebengebäude" });
  if (truthy(c.garageJa) || truthy(c.garage)) features.push({ kind: "garage", name: "Garage" });
  if (truthy(c.carportJa) || truthy(c.carport)) features.push({ kind: "carport", name: "Carport" });
  if (truthy(c.tiefgarageJa) || truthy(c.tiefgarage)) features.push({ kind: "tiefgarage", name: "Tiefgarage" });

  // Stellplätze je Art: Auswahl aus `stellplatzArten`, Lage und Anzahl aus
  // `stellplatzDetails`. Die Auswahl wird als JSON-Array gespeichert
  // (`'["Garage"]'`) — der frühere reine Komma-Split traf davon nie etwas, die
  // Kacheln blieben leer. Die Komma-Form bleibt als Lesepfad erhalten.
  const arten = parseStellplatzArten(c.stellplatzArten);
  const details = parseStellplatzDetails(c.stellplatzDetails);
  for (const art of arten) {
    if (!STELLPLATZ_LIST.includes(art)) continue;
    const kind = art.toLowerCase();
    const key = stellplatzArtKey(art);
    const hint = key ? stellplatzDetailText(details[key]) : "";
    // Dieselbe Art kann schon über den Altlast-Lesepfad (`garageJa`) stehen —
    // dann kommt die Kachel nicht doppelt, bekommt aber Anzahl und Lage dazu.
    const vorhanden = features.find((f) => f.kind === kind);
    if (vorhanden) {
      if (hint && !vorhanden.hint) vorhanden.hint = hint;
      continue;
    }
    features.push({ kind, name: art, hint: hint || undefined });
  }
  // Die frühere Sammelangabe galt für alle Arten zusammen. Sie erscheint nur
  // noch, solange es keine Angaben je Art gibt — sonst stünde dieselbe Zahl
  // zweimal auf der Seite.
  if (c.stellplatzAnzahl && Object.keys(details).length === 0) {
    features.push({
      kind: "stellplatz",
      name: `${c.stellplatzAnzahl} Stellplätze`,
      hint: c.stellplatzLage || undefined,
    });
  }
  // Gesamtfläche (xlsx Z. 7, Spalte X „x, 5 u. 7").
  if (c.gesamtflaeche) {
    features.push({ kind: "flaeche", name: `${c.gesamtflaeche} m² Gesamtfläche` });
  }

  return (
    <div className={card.cardSection}>
      <div className={card.card}>
        <span className={card.label}>Hauptobjekt</span>
        <span className={styles.addressLine}>{risk.name || `${risk.street}, ${risk.city}`}</span>
        <span className={card.helper}>
          {risk.street}, {risk.postalCode} {risk.city}
        </span>
      </div>
      {features.length === 0 ? (
        <div className={card.helper}>
          Keine Anbauten / Garagen / Carports / Tiefgarage / Stellplätze am Risikoobjekt erfasst.
        </div>
      ) : (
        <div className={styles.list}>
          {features.map((f, i) => (
            <div key={`${f.kind}-${i}`} className={styles.feature}>
              <div className={styles.iconBox}>{featureIcon(f.kind)}</div>
              <div className={styles.text}>
                <span className={styles.name}>{f.name}</span>
                {f.hint && <span className={styles.hint}>{f.hint}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── GewerbeTypBlock ────────────────────────────────────────
const useGewerbeStyles = makeStyles({
  pillRow: { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: "8px", rowGap: "8px" },
  bigTile: {
    backgroundColor: "#f5eef6",
    ...shorthands.border("2px", "dashed", BRAND.purple),
    ...shorthands.borderRadius("12px"),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
  },
  iconArea: {
    width: "70px",
    height: "60px",
    backgroundColor: BRAND.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...shorthands.borderRadius("10px"),
  },
  textArea: { display: "flex", flexDirection: "column", rowGap: "4px" },
  title: { fontFamily: BRAND.fontAccent, fontSize: "1.4rem", color: BRAND.ink, lineHeight: 1.1 },
  subtitle: { fontSize: "0.85rem", color: BRAND.muted },
});

const gewerbeIcon = (typ: string) => {
  const t = typ.toLowerCase();
  if (t.includes("logistik"))
    return (
      <svg width="44" height="36" viewBox="0 0 60 48" fill="none">
        <rect x="2" y="14" width="36" height="28" fill={BRAND.purple} stroke={BRAND.ink} strokeWidth="1.5" />
        <rect x="38" y="22" width="20" height="20" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.5" />
        <circle cx="14" cy="42" r="4" fill={BRAND.ink} />
        <circle cx="50" cy="42" r="4" fill={BRAND.ink} />
      </svg>
    );
  if (t.includes("büro") || t.includes("buero"))
    return (
      <svg width="44" height="40" viewBox="0 0 60 48" fill="none">
        <rect x="6" y="4" width="48" height="40" fill={BRAND.purple} />
        <rect x="12" y="10" width="8" height="8" fill={BRAND.bg} />
        <rect x="24" y="10" width="8" height="8" fill={BRAND.bg} />
        <rect x="36" y="10" width="8" height="8" fill={BRAND.bg} />
        <rect x="12" y="22" width="8" height="8" fill={BRAND.bg} />
        <rect x="24" y="22" width="8" height="8" fill={BRAND.bg} />
        <rect x="36" y="22" width="8" height="8" fill={BRAND.bg} />
      </svg>
    );
  if (t.includes("handel"))
    return (
      <svg width="44" height="40" viewBox="0 0 60 48" fill="none">
        <path d="M4 16h52l-4 28H8z" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.5" />
        <path d="M18 16v-4a12 12 0 1 1 24 0v4" stroke={BRAND.purple} strokeWidth="2" fill="none" />
      </svg>
    );
  if (t.includes("produktion"))
    return (
      <svg width="44" height="40" viewBox="0 0 60 48" fill="none">
        <path d="M4 44V20l16 8V20l16 8V12l20 8v24z" fill={BRAND.purple} />
        <rect x="8" y="36" width="6" height="6" fill={BRAND.gold} />
        <rect x="22" y="36" width="6" height="6" fill={BRAND.gold} />
        <rect x="38" y="36" width="6" height="6" fill={BRAND.gold} />
      </svg>
    );
  if (t.includes("hotel"))
    return (
      <svg width="44" height="40" viewBox="0 0 60 48" fill="none">
        <rect x="8" y="6" width="44" height="38" fill={BRAND.purple} />
        <rect x="14" y="12" width="6" height="6" fill={BRAND.gold} />
        <rect x="22" y="12" width="6" height="6" fill={BRAND.gold} />
        <rect x="30" y="12" width="6" height="6" fill={BRAND.gold} />
        <rect x="38" y="12" width="6" height="6" fill={BRAND.gold} />
        <rect x="14" y="22" width="6" height="6" fill={BRAND.gold} />
        <rect x="22" y="22" width="6" height="6" fill={BRAND.gold} />
        <rect x="30" y="22" width="6" height="6" fill={BRAND.gold} />
        <rect x="38" y="22" width="6" height="6" fill={BRAND.gold} />
        <rect x="26" y="34" width="8" height="10" fill={BRAND.bg} />
      </svg>
    );
  return (
    <svg width="44" height="40" viewBox="0 0 60 48" fill="none">
      <rect x="6" y="10" width="48" height="34" fill={BRAND.purple} />
      <rect x="22" y="6" width="16" height="8" fill={BRAND.gold} />
    </svg>
  );
};

export const GewerbeTypBlock = ({ risk }: { risk: RiskSummary }) => {
  const styles = useGewerbeStyles();
  const card = useSharedStyles();
  const c = risk.characteristics;
  const value = c.gewerbeTyp || c.typ || "";
  const matched =
    GEWERBE_TYPEN.find((g) => g.toLowerCase() === value.toLowerCase()) ||
    GEWERBE_TYPEN.find((g) => value.toLowerCase().includes(g.toLowerCase().slice(0, 5))) ||
    value ||
    "Sonstige";

  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Da das Risikoobjekt als Gewerbe (100%) klassifiziert ist, wird der spezifische Gewerbe-Typ
        erfasst.
      </div>
      <div className={styles.bigTile}>
        <div className={styles.iconArea}>{gewerbeIcon(matched)}</div>
        <div className={styles.textArea}>
          <span className={styles.title}>{matched}</span>
          <span className={styles.subtitle}>Vom Risikoobjekt übernommen</span>
        </div>
      </div>
    </div>
  );
};

// ── BaujahrBlock ────────────────────────────────────────
export const BaujahrBlock = ({
  risk,
  value,
  onChange,
}: {
  risk: RiskSummary;
  value: string;
  onChange: (v: string) => void;
}) => {
  const card = useSharedStyles();
  const seedBaujahr = risk.characteristics.baujahr || "";
  useEffect(() => {
    if (!value && seedBaujahr) onChange(seedBaujahr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedBaujahr]);
  return (
    <div className={card.card}>
      <div className={card.field}>
        <label className={card.label} htmlFor="baujahr">
          Baujahr des Gebäudes
        </label>
        <input
          id="baujahr"
          type="number"
          inputMode="numeric"
          placeholder="z. B. 1985"
          className={card.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {seedBaujahr && (
          <span className={card.helper}>Aus Risikoobjekt vorbelegt: {seedBaujahr}</span>
        )}
      </div>
    </div>
  );
};

// ── VersichererPicker ────────────────────────────────────────
const useVersichererStyles = makeStyles({
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("12px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    transitionProperty: "border-color, background-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  tileActive: {
    ...shorthands.border("2px", "dashed", BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: "0 4px 14px rgba(77,32,81,0.18)",
  },
  wordmark: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.6rem",
    color: BRAND.purple,
    flexGrow: 1,
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
  },
  check: {
    width: "26px",
    height: "26px",
    borderRadius: "999px",
    backgroundColor: BRAND.gold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.ink,
    flexShrink: 0,
    fontWeight: 700,
  },
});

export const VersichererPicker = ({
  selected,
  onSelect,
}: {
  selected: boolean;
  onSelect: () => void;
}) => {
  const styles = useVersichererStyles();
  const card = useSharedStyles();
  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Aktuell ist nur Generali als Versicherer im System hinterlegt. Weitere Gesellschaften
        werden später ergänzt.
      </div>
      <button
        type="button"
        className={mergeClasses(styles.tile, selected && styles.tileActive)}
        onClick={onSelect}
      >
        <span className={styles.wordmark}>Generali</span>
        <span className={styles.hint}>Generali Deutschland Versicherung AG</span>
        {selected && (
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
        )}
      </button>
    </div>
  );
};
